/**
 * The store cart's arithmetic and rules, kept pure so they can be tested
 * without rendering anything.
 *
 * The rules that matter:
 *  * the cart can never hold more of a product than the store says is
 *    available — across ALL its size lines, because stock is per product;
 *  * a sized product is one line per (product, size); an unsized product is
 *    one line;
 *  * totals include each product's own tax rate, line by line;
 *  * the 48-hour hold is presented in whole hours, never negative.
 */

import { parseBackendDate } from './dateUtils';

export interface CartProduct {
  id: string;
  name: string;
  sellingPrice: number;
  taxPercent: number;
  availableQuantity: number;
  sizes: string[];
}

export interface CartLine {
  product: CartProduct;
  size?: string;
  quantity: number;
}

/** Everything in the cart for one product, across all its sizes. */
export function quantityInCart(cart: CartLine[], productId: string): number {
  return cart
    .filter(line => line.product.id === productId)
    .reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * Adds one unit, merging into an existing (product, size) line. Returns the
 * cart unchanged when the product's availability is already fully claimed.
 */
export function addToCart(cart: CartLine[], product: CartProduct, size?: string): CartLine[] {
  if (quantityInCart(cart, product.id) >= product.availableQuantity) return cart;

  const existing = cart.find(line => line.product.id === product.id && line.size === size);
  if (existing) {
    return cart.map(line =>
      line === existing ? { ...line, quantity: line.quantity + 1 } : line);
  }
  return [...cart, { product, size, quantity: 1 }];
}

/**
 * Steps a line's quantity. Stepping down to zero removes the line; stepping up
 * past the product's availability (counted across all its lines) is a no-op.
 */
export function changeQuantity(cart: CartLine[], line: CartLine, delta: number): CartLine[] {
  return cart.flatMap(candidate => {
    if (candidate !== line) return [candidate];
    if (delta > 0 && quantityInCart(cart, candidate.product.id) >= candidate.product.availableQuantity) {
      return [candidate];
    }
    const quantity = candidate.quantity + delta;
    return quantity <= 0 ? [] : [{ ...candidate, quantity }];
  });
}

/** The order total including each product's own tax rate. */
export function cartTotal(cart: CartLine[]): number {
  return cart.reduce((sum, line) => {
    const net = line.product.sellingPrice * line.quantity;
    return sum + net + net * (line.product.taxPercent / 100);
  }, 0);
}

/** Whole hours left on a 48-hour hold; 0 once it has lapsed, never negative. */
export function hoursLeft(reservedUntil: string, now: Date = new Date()): number {
  // parseBackendDate, not new Date: reservedUntil carries no timezone marker, so a
  // plain parse put the deadline out by the device's UTC offset - a hold could read
  // as 5 hours longer (or shorter) than it really is (see dateUtils).
  const deadline = parseBackendDate(reservedUntil);
  if (!deadline) return 0;
  return Math.max(0, Math.round((deadline.getTime() - now.getTime()) / 3_600_000));
}
